import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Stack, Typography, Paper, TextField, Button, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer, Chip, Box, InputAdornment, CircularProgress,
  TablePagination, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, Link as MuiLink, Switch, FormControlLabel, Select, MenuItem,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import SyncIcon from "@mui/icons-material/SyncOutlined";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import LaunchIcon from "@mui/icons-material/OpenInNewOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOfferOutlined";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUpOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import { ChevronUp, ChevronDown } from "lucide-react";
import { apiList, apiPost, ApiError, apiFetch } from "../api/client";
import { cacheGet, cacheSet, cacheKey, cacheInvalidate } from "../api/cache";
import { usePersistedState } from "../api/persistedState";
import EditableTextCell from "../components/EditableTextCell";
import ImageGalleryCell from "../components/ImageGalleryCell";
import { useToast } from "../ui/Toaster";

const UNDERCUT_LS_KEY = "bidrl_listings_undercut";

function useDebounced(value, ms = 300) {
  const [d, setD] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setD(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return d;
}

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// Quick-drop chips: 1 "rounded" button + 4 fixed-dollar drop buttons.
// First chip = current rounded down to the next $5 boundary minus 1¢
// (the X9.99 / X4.99 psychological price). Subsequent chips subtract a
// fixed dollar amount from that rounded base. This avoids the duplicate
// problem that plagued the earlier step-based approach — at $73.29 the
// $25 and $50 step rules both landed on $49.99, and at $30 the $25 and
// $50 step rules both landed on $24.99.
const QUICK_DROP_MIN = 14.99;
const QUICK_DROP_DELTAS = [0, 5, 10, 15, 25];

function quickDropTargets(currentPrice, minPrice = QUICK_DROP_MIN) {
  const c = Number(currentPrice) || 0;
  if (c < minPrice + 0.01) return [];
  // "Base" = round current down to next $5 boundary, minus 1¢. So:
  //   $73.29 -> $69.99
  //   $30.00 -> $29.99
  //   $27.48 -> $24.99
  const base = Math.floor(c / 5) * 5 - 0.01;
  if (base <= 0) return [];

  const seen = new Set();
  const out = [];
  for (const delta of QUICK_DROP_DELTAS) {
    const target = Number((base - delta).toFixed(2));
    if (target < minPrice) continue;   // below operator's floor
    if (target >= c) continue;          // must be a strict price reduction
    if (seen.has(target)) continue;     // dedupe (esp. at low prices)
    seen.add(target);
    out.push({ delta, target });
  }
  return out;
}

// Compact "Xm ago / Xh ago / Xd ago" for the inline timestamps under
// price + lowest-comp cells. Stays terse on purpose — the cell only
// has room for one short string. Anything older than 30 days renders
// as "(date)" so the operator doesn't have to do mental math.
function relTime(unixSeconds) {
  if (!unixSeconds) return "";
  const s = Math.max(0, Math.floor(Date.now() / 1000) - Number(unixSeconds));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  try {
    return new Date(Number(unixSeconds) * 1000).toLocaleDateString();
  } catch { return ""; }
}

// Map our free-text condition string ("Open Box", "Used", "For Parts",
// etc.) to eBay's numeric ``LH_ItemCondition`` filter id. Returns ``null``
// for unknown/empty so the search URL omits the filter rather than
// silently narrowing to a wrong condition.
//
// Reference (eBay's published condition IDs):
//   1000  Brand New
//   1500  New (Other)         ← "Open Box" lands here
//   1750  New with defects
//   2500  Seller Refurbished
//   3000  Used
//   7000  For Parts or Not Working
function ebayConditionFilter(condition) {
  const c = String(condition || "").toLowerCase().trim();
  if (!c) return null;
  if (c.includes("open box")) return "1500";
  if (c.includes("for parts") || c.includes("not working")) return "7000";
  if (c.includes("refurbished") || c.includes("refurb")) return "2500";
  if (c.startsWith("new")) return "1000";
  if (c.includes("used") || c.includes("pre-owned") || c.includes("preowned")) return "3000";
  return null;
}

// eBay's search ``_nkw`` param expects form-encoded values (spaces as
// ``+``), not full URI encoding (``%20``). Browsers handle both, but
// ``%20``-encoded queries get echoed back by eBay's UI as the literal
// "%20", which makes the breadcrumb / "search again" box show
// "Littlest%20Pet%20Shop…" — confusing when troubleshooting.
const ebayEncode = (s) => encodeURIComponent(s).replace(/%20/g, '+');

// Build an eBay search URL from a free-form title.
//
//   sold=true   → completed/sold view (``LH_Sold=1&LH_Complete=1``)
//   sold=false  → active Buy-It-Now-only listings
//   condition   → narrows to listings of the same condition as the
//                 caller's row (so a New listing doesn't compare
//                 itself against Used comps)
//   sortLowest  → ``_sop=15`` (Price + Shipping: lowest first) instead
//                 of eBay's "Best Match" default
function ebaySearchUrl(title, { sold = false, condition = null, sortLowest = false } = {}) {
  const q = (title || "").trim();
  if (!q) return null;
  const parts = [`_nkw=${ebayEncode(q)}`];
  if (sold) {
    parts.push("LH_Sold=1", "LH_Complete=1");
  } else {
    parts.push("LH_BIN=1");
  }
  const cond = ebayConditionFilter(condition);
  if (cond) parts.push(`LH_ItemCondition=${cond}`);
  if (sortLowest) parts.push("_sop=15");
  return `https://www.ebay.com/sch/i.html?${parts.join("&")}`;
}

function TierChip({ emoji }) {
  if (emoji === "❗") return <Chip size="small" label="Critical" color="error" variant="outlined" />;
  if (emoji === "⚠️") return <Chip size="small" label="Warning" color="warning" variant="outlined" />;
  if (emoji === "✅") return <Chip size="small" label="OK" color="success" variant="outlined" />;
  return <Chip size="small" label="—" variant="outlined" />;
}

function DiscountModal({ open, onClose, listing, onApplied }) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [percent, setPercent] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setPercent("");
  }, [open]);

  const p = parseInt(percent || "0", 10);
  const newPrice = listing && isFinite(p) && p > 0 && p < 100
    ? Math.max(0, Math.round(listing.price * (1 - p / 100)) - 0.01).toFixed(2)
    : null;

  const apply = async () => {
    if (!listing) return;
    if (!isFinite(p) || p <= 0 || p >= 100) {
      toastError("Enter a percentage between 1 and 99.");
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost(`/api/discount/${encodeURIComponent(listing.item_id)}`, {
        sku: listing.lot_id,
        percent: p,
        price: listing.price,
      });
      if (res?.success) {
        toastSuccess("Discount applied", `${listing.lot_id} → $${(res.new_price ?? newPrice)}`);
        onApplied?.();
        onClose?.();
      } else {
        toastError("Discount rejected", res?.error || res?.message || "");
      }
    } catch (e) {
      toastError("Failed to apply", e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Apply discount</DialogTitle>
      <DialogContent>
        {listing && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {listing.title || listing.lot_id} • current price {money(listing.price)}
          </Typography>
        )}
        <Stack direction="row" alignItems="flex-end" gap={2}>
          <TextField
            type="number"
            inputProps={{ min: 1, max: 99 }}
            placeholder="% off"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            size="small"
            autoFocus
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LocalOfferIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {newPrice && (
            <Box sx={{ textAlign: 'right', minWidth: 90 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                New price
              </Typography>
              <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                ${newPrice}
              </Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={apply}
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function UndercutModal({ open, onClose, listing, onApplied }) {
  const { error: toastError, success: toastSuccess } = useToast();
  const [busy, setBusy] = useState(false);

  if (!listing) return null;
  const currentPrice = Number(listing.price) || 0;
  const target = Number(listing.undercut_target);
  const competitor = Number(listing.lowest_competitor_price);
  // Percent off needed to get from current price down to undercut target.
  // Reuses the existing /api/discount endpoint, which does
  // newPrice = price * (1 - percent/100), then rounds to .99.
  // We compute the percent that achieves >= target and let the server's
  // .99 nudge land us at or just under it.
  const percentNeeded = currentPrice > 0 && isFinite(target) && target < currentPrice
    ? Math.max(1, Math.min(99, Math.ceil((1 - target / currentPrice) * 100)))
    : null;

  const apply = async () => {
    if (!percentNeeded) {
      toastError("Nothing to undercut", "Listing is already at or below competitor.");
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost(`/api/discount/${encodeURIComponent(listing.item_id)}`, {
        sku: listing.lot_id,
        percent: percentNeeded,
        price: currentPrice,
      });
      if (res?.success) {
        toastSuccess("Undercut applied", `${listing.lot_id} → $${res.new_price ?? target.toFixed(2)}`);
        onApplied?.();
        onClose?.();
      } else {
        toastError("Undercut rejected", res?.error || res?.message || "");
      }
    } catch (e) {
      toastError("Failed to apply", e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Undercut competitor</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {listing.title || listing.lot_id}
        </Typography>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Lowest competitor</Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {isFinite(competitor) ? money(competitor) : "—"}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>Current price</Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {money(currentPrice)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>New price</Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {isFinite(target) ? money(target) : "—"}
          </Typography>
        </Stack>
        {percentNeeded != null && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Sends ~{percentNeeded}% discount to eBay (server rounds to .99).
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={apply}
          variant="contained"
          disabled={busy || !percentNeeded}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Undercut
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PromoteModal({ open, onClose, listing, onConfirm, busy }) {
  if (!listing) return null;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Promote listing</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Apply promoted listings to <strong>{listing.title || listing.lot_id}</strong>?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          onClick={() => onConfirm(listing)}
          variant="contained"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Promote
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Trimmed from 11 columns to 8 to reduce visual noise. Folded:
//   - "Tier" → small emoji chip on the Image cell's corner
//   - "Spend" → cost tooltip on the title (no per-row pixels eaten)
//   - "Views" + "Days" → single "Activity" column rendered as
//     "12 views / 3d" with the tier color
// Added:
//   - Timestamps under "Price" (edited Xm ago) and "Lowest"
//     (comp checked Xh ago)
const COLUMNS = [
  { key: "image_urls",  header: "",          width: 80,  sortable: false },
  { key: "title",       header: "Listing",   sortable: true },
  { key: "price",       header: "Price",     width: 120, sortable: true,  align: "right" },
  { key: "quantity",    header: "Qty",       width: 80,  sortable: false, align: "right" },
  { key: "condition",   header: "Condition", width: 110, sortable: false },
  { key: "lowest",      header: "Lowest",    width: 110, sortable: false, align: "right" },
  // Sort key is days_unsold (server's allowed-key list already covers
  // it). Click header → stalest-first / newest-first; the cell renders
  // both views and days unsold together with the tier color.
  { key: "days_unsold", header: "Activity",  width: 110, sortable: true,  align: "right" },
  { key: "actions",     header: "",          width: 320, sortable: false, align: "right" },
];

// Frontend dropdown choices. Backend resolves to the right per-category enum
// (see _CONDITION_LABEL_TO_NOTES + choose_condition() server-side).
const CONDITION_CHOICES = [
  { value: "new",      label: "New" },
  { value: "open_box", label: "Open Box" },
  { value: "used",     label: "Used" },
  { value: "parts",    label: "Parts" },
];

// Map the items.condition free-text we render in the table back to one of
// the dropdown choices so the Select shows the current value. Open Box is
// distinct from New — collapsing them hides the real listing condition.
// Also accepts the raw dropdown values ("open_box", "parts") so the
// optimistic update in savePatch reads the same current value without a
// flash while the server response is in flight.
function conditionToChoice(c) {
  const s = String(c || "").toLowerCase();
  if (!s) return "";
  if (s === "open_box" || s.includes("open box") || s.includes("like new") || s === "new_other") return "open_box";
  if (s === "parts" || s.includes("part") || s === "for parts") return "parts";
  if (s === "new" || s.startsWith("new")) return "new";
  if (s === "used") return "used";
  return "used";
}

export default function EbayListings() {
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // True while the operator's "Refresh" button is force-syncing from
  // eBay (POST /api/listings/refresh). Separate from ``loading`` so the
  // button can show the "Syncing eBay…" label distinctly from a normal
  // page-data reload.
  const [forceSyncing, setForceSyncing] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePersistedState("bidrl_listings_page_size", 50);
  const [search, setSearch] = useState("");
  // Default sort: stalest listings first (highest days_unsold). The
  // old default ("tier_emoji") column was folded into the title cell
  // and is no longer a clickable column header.
  const [sortKey, setSortKey] = usePersistedState("bidrl_listings_sort_key", "days_unsold");
  const [sortDir, setSortDir] = usePersistedState("bidrl_listings_sort_dir", "desc");
  // Imbalance filter — array of {overlisted, underlisted, balanced}. Empty
  // = show all. Persisted so refreshing the page keeps the user's view.
  const [imbalanceFilter, setImbalanceFilter] = usePersistedState(
    "bidrl_listings_imbalance_filter", []
  );
  // Condition + category dropdowns and age-bucket filter. Server applies
  // them before pagination so the footer count reflects post-filter reality.
  const [conditionFilter, setConditionFilter] = usePersistedState("bidrl_listings_condition_filter", []);
  const [categoryFilter,  setCategoryFilter]  = usePersistedState("bidrl_listings_category_filter",  []);
  const [ageFilter,       setAgeFilter]       = usePersistedState("bidrl_listings_age_filter",       "");
  // Facets sent by the backend (computed pre-filter, always the full set).
  // Persisted so a reload doesn't blank the dropdowns while the API is
  // still answering.
  const [facets, setFacets] = usePersistedState("bidrl_listings_facets", { categories: [], conditions: [] });
  // Cumulative "seen this session (and last session, and…)" set of values
  // — persisted to localStorage as arrays because Sets don't survive
  // JSON.stringify. Fixes "filter to Used → other conditions vanish from
  // the dropdown" (which was caused by deriving from currently-visible
  // rows only). Each fetch adds any new values but never removes.
  const [seenFacets, setSeenFacets] = usePersistedState(
    "bidrl_listings_seen_facets",
    { conditions: [], categories: [] },
  );
  useEffect(() => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    setSeenFacets((prev) => {
      const prevC = new Set(prev.conditions || []);
      const prevK = new Set(prev.categories || []);
      let changed = false;
      for (const r of rows) {
        const c = (r.condition || '').trim();
        const k = (r.category_name || '').trim();
        if (c && !prevC.has(c)) { prevC.add(c); changed = true; }
        if (k && !prevK.has(k)) { prevK.add(k); changed = true; }
      }
      return changed
        ? { conditions: Array.from(prevC), categories: Array.from(prevK) }
        : prev;
    });
  }, [rows, setSeenFacets]);
  // Effective options = backend facets ∪ historical seen values ∪
  // currently-selected values. The last term matters: if a user reloads
  // the page while filtered to Used AND the backend hasn't answered yet
  // AND the seen set is empty (fresh browser), the dropdown would still
  // show "Used" so they can un-check it — otherwise they'd be trapped.
  const effectiveFacets = useMemo(() => ({
    conditions: Array.from(new Set([
      ...(facets.conditions || []),
      ...(seenFacets.conditions || []),
      ...(Array.isArray(conditionFilter) ? conditionFilter : []),
    ])).sort(),
    categories: Array.from(new Set([
      ...(facets.categories || []),
      ...(seenFacets.categories || []),
      ...(Array.isArray(categoryFilter) ? categoryFilter : []),
    ])).sort(),
  }), [facets, seenFacets, conditionFilter, categoryFilter]);

  const [discountTarget, setDiscountTarget] = useState(null);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [promoting, setPromoting] = useState(false);
  const [undercutTarget, setUndercutTarget] = useState(null);
  // Per-CELL busy state, keyed as ``${lot_id}:${field}``. Used to be
  // per-row, which blocked the operator from editing qty on a row
  // whose price was still saving. Per-cell lets the row stay live —
  // and parallel saves are fine because each PATCH only touches one
  // field server-side. The Set is also stable across renders so a
  // race between two saves on the same cell still ends up with one
  // in-flight indicator (the second add() is a no-op on existing key).
  const [savingFields, setSavingFields] = useState(() => new Set());

  const cellKey = (sku, field) => `${sku}:${field}`;

  const savePatch = useCallback(async (row, patch) => {
    const sku = row.lot_id;
    if (!sku) return;
    const fields = Object.keys(patch);
    const keys = fields.map((f) => cellKey(sku, f));
    setSavingFields((prev) => {
      const n = new Set(prev);
      keys.forEach((k) => n.add(k));
      return n;
    });
    // Optimistic update so the row reflects the change immediately.
    // Snapshot the pre-patch values for THIS specific field set so
    // we can revert cleanly even if other saves on this row are
    // concurrently mutating sibling fields. Without the per-field
    // snapshot, reverting after a failure would restore the whole
    // row to its state before BOTH edits — losing the sibling edit.
    const prevFieldValues = {};
    fields.forEach((f) => { prevFieldValues[f] = row[f]; });
    setRows((cur) =>
      cur.map((r) => (r.lot_id === sku ? { ...r, ...patch } : r))
    );
    try {
      const res = await apiFetch(`/api/listings/${encodeURIComponent(sku)}`, {
        method: "PATCH",
        json: patch,
      });
      if (res?.success) {
        const applied = res.applied || {};
        cacheInvalidate("/api/listings");
        const fields = Object.keys(applied).join(", ") || "saved";
        toastSuccess("Updated", `${sku} → ${fields}`);
        // Reconcile + recompute the imbalance flag locally. The PATCH
        // doesn't echo back ``listing_imbalance`` (it'd require redoing
        // the whole group join server-side) so we derive it from the
        // new quantity vs the row's known unsold_remaining target.
        // When an imbalance filter is active and the row's new state no
        // longer matches it, drop the row — otherwise fixed listings
        // would linger in the "Over-listed" view until the next reload,
        // which is the bug behind the over-correction problem.
        setRows((cur) => {
          const activeImbalanceFilter =
            Array.isArray(imbalanceFilter) && imbalanceFilter.length > 0;
          return cur
            .map((r) => {
              if (r.lot_id !== sku) return r;
              const merged = { ...r, ...applied };
              if (Number.isFinite(Number(applied.quantity))) {
                const q = Number(applied.quantity);
                const target = Number(merged.unsold_remaining);
                if (Number.isFinite(target)) {
                  merged.listing_imbalance =
                    q > target ? "overlisted" :
                    q < target ? "underlisted" :
                    "balanced";
                }
              }
              return merged;
            })
            .filter((r) => {
              if (r.lot_id !== sku) return true;
              if (!activeImbalanceFilter) return true;
              return imbalanceFilter.includes(r.listing_imbalance);
            });
        });
        // Total drives the pagination footer; if we dropped this row
        // from the visible set, reflect that in the count too.
        if (Array.isArray(imbalanceFilter) && imbalanceFilter.length > 0) {
          setTotal((t) => {
            // Compare against the row's *new* imbalance — only
            // decrement when the filter no longer accepts it.
            const q = Number(applied.quantity);
            const target = Number(row.unsold_remaining);
            if (!Number.isFinite(q) || !Number.isFinite(target)) return t;
            const newKind =
              q > target ? "overlisted" :
              q < target ? "underlisted" :
              "balanced";
            return imbalanceFilter.includes(newKind) ? t : Math.max(0, t - 1);
          });
        }
        // The PATCH response also carries any partial-failure warnings
        // (e.g. "eBay rejected the price update"). Surface each as a
        // toast so the operator notices when a save half-succeeded.
        const warnings = Array.isArray(res.warnings) ? res.warnings : [];
        warnings.forEach((w) => toastError("Partial save", w));
      } else {
        // Server explicitly said no — revert ONLY the field(s) we tried
        // to patch in this call. Leaves sibling fields edited by other
        // in-flight saves alone.
        setRows((cur) =>
          cur.map((r) => (r.lot_id === sku ? { ...r, ...prevFieldValues } : r))
        );
        toastError("Couldn't save", res?.error || "Update failed");
      }
    } catch (e) {
      setRows((cur) =>
        cur.map((r) => (r.lot_id === sku ? { ...r, ...prevFieldValues } : r))
      );
      toastError("Couldn't save", e instanceof ApiError ? e.message : String(e));
    } finally {
      setSavingFields((prev) => {
        const n = new Set(prev);
        keys.forEach((k) => n.delete(k));
        return n;
      });
    }
  }, [toastError, toastSuccess, imbalanceFilter]);

  // Persisted "show competitor prices" toggle. Default OFF so the page
  // doesn't blow the eBay quota every time someone navigates here.
  const [showUndercut, setShowUndercut] = useState(() => {
    try {
      return localStorage.getItem(UNDERCUT_LS_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(UNDERCUT_LS_KEY, showUndercut ? "1" : "0");
    } catch {
      /* ignore quota / privacy-mode errors */
    }
  }, [showUndercut]);
  // "Undercut only" filter — narrows the view to listings where a
  // competitor is cheaper than us right now. Backend reads this off
  // the per-row ``is_undercut`` flag computed during the bulk
  // competitor lookup, so the pagination footer reflects real counts.
  // Enabling this auto-fetches competitor data even if the operator
  // hadn't toggled showUndercut, since the filter is meaningless without it.
  const [undercutOnly, setUndercutOnly] = useState(false);

  const debounced = useDebounced(search, 300);

  const load = useCallback(
    async (signal) => {
      const params = {
        page,
        page_size: pageSize,
        q: debounced,
        sort: sortKey,
        order: sortDir,
      };
      if (showUndercut) params.with_undercut = "true";
      if (undercutOnly) params.undercut_only = "true";
      if (Array.isArray(imbalanceFilter) && imbalanceFilter.length > 0) {
        params.imbalance = imbalanceFilter.join(",");
      }
      if (Array.isArray(conditionFilter) && conditionFilter.length > 0) {
        params.condition = conditionFilter.join(",");
      }
      if (Array.isArray(categoryFilter) && categoryFilter.length > 0) {
        params.category = categoryFilter.join(",");
      }
      if (ageFilter) params.age = ageFilter;
      const ck = cacheKey("/api/listings", params);
      const cached = cacheGet(ck);
      if (cached) {
        setRows(cached.data.items || []);
        setTotal(cached.data.total || 0);
        if (cached.data.facets) setFacets(cached.data.facets);
        setLoading(false);
      } else {
        setLoading(true);
      }
      try {
        const res = await apiList("/api/listings", params, { signal });
        if (signal?.aborted) return;
        setRows(res.items || []);
        setTotal(res.total || 0);
        if (res.facets) setFacets(res.facets);
        cacheSet(ck, res);
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (!cached) {
          toastError("Couldn't load listings", e instanceof ApiError ? e.message : String(e));
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [page, pageSize, debounced, sortKey, sortDir, showUndercut, undercutOnly, imbalanceFilter, conditionFilter, categoryFilter, ageFilter, toastError]
  );

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    return () => ctrl.abort();
  }, [load]);

  useEffect(() => { setPage(1); }, [debounced, sortKey, sortDir, pageSize, imbalanceFilter, undercutOnly, conditionFilter, categoryFilter, ageFilter]);

  const promote = useCallback(async (row) => {
    if (!row.item_id) {
      toastError("No eBay item id available for this listing.");
      return;
    }
    setPromoting(true);
    try {
      const res = await apiPost(`/api/promote/${encodeURIComponent(row.item_id)}`);
      if (res?.success) toastSuccess("Promotion applied", res.message);
      else toastError("Couldn't promote", res?.message || "");
    } catch (e) {
      toastError("Promote failed", e instanceof ApiError ? e.message : String(e));
    } finally {
      setPromoting(false);
      setPromoteTarget(null);
    }
  }, [toastSuccess, toastError]);

  // Track which lot_id is currently being refreshed (End + RelistFixedPriceItem)
  // so we can disable the button and show a spinner while the eBay round-trip
  // happens. Uses a Set so multiple concurrent relists don't stomp each other.
  const [relisting, setRelisting] = useState(() => new Set());
  const relistOne = useCallback(async (row) => {
    if (!row.lot_id) return;
    setRelisting((prev) => new Set(prev).add(row.lot_id));
    try {
      const res = await apiPost(`/api/listings/${encodeURIComponent(row.lot_id)}/relist`);
      if (!res?.success) {
        toastError("Relist failed", res?.error || "Unknown error");
        return;
      }
      toastSuccess(
        "Listing refreshed",
        `${row.lot_id}: ${res.old_ebay_item_id} → ${res.new_ebay_item_id}`,
      );
      // Optimistically reset the row's days_unsold so the button greys out
      // right away — a background refetch will bring back the true state
      // from the server on the next page load.
      setRows((cur) =>
        cur.map((r) => (r.lot_id === row.lot_id
          ? { ...r, days_unsold: 0, item_id: res.new_ebay_item_id }
          : r)),
      );
    } catch (e) {
      toastError("Relist failed", e instanceof ApiError ? e.message : String(e));
    } finally {
      setRelisting((prev) => {
        const n = new Set(prev);
        n.delete(row.lot_id);
        return n;
      });
    }
  }, [toastSuccess, toastError]);

  const onHeaderClick = useCallback((col) => {
    if (!col.sortable) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }, [sortKey, setSortDir, setSortKey]);

  const renderCell = (row, key) => {
    // Each editable field tracks its own busy state via the
    // ``savingFields`` set. Renderers pass the appropriate flag to
    // ``EditableTextCell``. ``rowBusy`` is the OR of all fields on
    // this row — used by the chip-click qty fix below and by the
    // condition Select which doesn't distinguish field.
    const fieldBusy = (field) => savingFields.has(`${row.lot_id}:${field}`);
    const rowBusy = (
      fieldBusy("price") || fieldBusy("quantity")
      || fieldBusy("title") || fieldBusy("condition")
    );
    const busy = rowBusy;  // back-compat alias for code below
    switch (key) {
      case "image_urls":
        // Folded the old "Tier" column onto the image cell — the
        // emoji renders as a small overlay so the operator can still
        // eyeball listing health without a dedicated column.
        return (
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <ImageGalleryCell imageUrlsStr={row.image_urls} thumbnailSize={48} />
            {row.tier_emoji && (
              <Tooltip
                title={
                  row.tier_emoji === "❗" ? "Critical" :
                  row.tier_emoji === "⚠️" ? "Warning" :
                  row.tier_emoji === "✅" ? "OK" : ""
                }
                placement="top"
              >
                <Box
                  component="span"
                  sx={{
                    position: 'absolute', bottom: -4, right: -4,
                    fontSize: 14, lineHeight: 1, userSelect: 'none',
                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
                  }}
                >
                  {row.tier_emoji}
                </Box>
              </Tooltip>
            )}
          </Box>
        );
      case "title": {
        // Inline-editable title with inventory deep-link as a small
        // icon alongside (so editing doesn't fight with link navigation).
        // Tooltip on the title shows the cost basis — the old "Spend"
        // column was folded in here to free a column.
        const spend = (Number(row.cost) || 0) + (Number(row.tax_cost) || 0) + (Number(row.premium_fee) || 0);
        // Group-level avg unit cost (backend computes across every lot in
        // the product+condition group). Only surface it as a distinct
        // number when the group has more than one lot AND the average
        // actually diverges from this row's spend — a single-lot group
        // would just repeat the spend value.
        const avgUnitCost = Number(row.avg_unit_cost) || 0;
        const groupLotCount = Number(row.group_lot_count) || 1;
        const showGroupAvg = groupLotCount > 1 && Math.abs(avgUnitCost - spend) >= 0.01;
        const costTip = showGroupAvg
          ? `Cost basis: ${money(spend)} (this lot)\nGroup avg: ${money(avgUnitCost)} across ${groupLotCount} lots`
          : `Cost basis: ${money(spend)} (cost + tax + premium)`;
        return (
          <Box sx={{ minWidth: 0, maxWidth: 420 }}>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{costTip}</span>} placement="top-start" arrow>
                  <Box>
                    <EditableTextCell
                      value={row.title || ""}
                      busy={fieldBusy("title")}
                      placeholder="Title (max 80)"
                      inputProps={{ maxLength: 80 }}
                      onCommit={(v) => savePatch(row, { title: v })}
                      displayValue={
                        <Typography
                          variant="body2"
                          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {row.title || "—"}
                        </Typography>
                      }
                    />
                  </Box>
                </Tooltip>
              </Box>
              {row.inventory_key && (
                <Tooltip title="Open inventory product">
                  <IconButton
                    size="small"
                    component={RouterLink}
                    to={`/admint/inventory/${encodeURIComponent(row.inventory_key)}`}
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: "text.secondary" }}
                  >
                    <LaunchIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontFamily: 'monospace',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
                pl: 0.5,
              }}
            >
              {row.lot_id}
              {showGroupAvg && (
                <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
                  · avg unit cost {money(avgUnitCost)} ({groupLotCount} lots)
                </Box>
              )}
            </Typography>
          </Box>
        );
      }
      case "price": {
        const edited = row.last_edited_at;
        const dropTargets = quickDropTargets(row.price);
        return (
          <Stack alignItems="flex-end" spacing={0.25}>
            <EditableTextCell
              value={row.price ?? ""}
              type="number"
              align="right"
              busy={fieldBusy("price")}
              inputProps={{ step: "0.01", min: "0.01" }}
              onCommit={(v) => {
                const n = Number(v);
                if (!Number.isFinite(n) || n <= 0) return;
                savePatch(row, { price: Number(n.toFixed(2)) });
              }}
              displayValue={
                <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>{money(row.price)}</Box>
              }
            />
            {/* Quick-drop chips. First chip = rounded base (delta=0),
                rest = base − $5/$10/$15/$25. Each labels the resulting
                price; the tooltip names the action + savings vs the
                current price. Dedupe ensures repeated values (common
                at round dollar amounts) collapse to one chip. */}
            {dropTargets.length > 0 && (
              <Stack direction="row" spacing={0.25} justifyContent="flex-end" flexWrap="wrap">
                {dropTargets.map(({ delta, target }) => {
                  const savings = (Number(row.price) || 0) - target;
                  const tooltip = delta === 0
                    ? `Round to $${target.toFixed(2)} (− $${savings.toFixed(2)})`
                    : `Drop $${delta} (to $${target.toFixed(2)}, − $${savings.toFixed(2)})`;
                  return (
                    <Tooltip key={delta} title={tooltip} placement="top" arrow>
                      <Chip
                        size="small"
                        label={`$${target.toFixed(2)}`}
                        clickable
                        variant="outlined"
                        // First chip (delta=0) is just psychological
                        // rounding — visually softer so the eye doesn't
                        // jump there first when the operator wants to
                        // make a real cut.
                        color={delta === 0 ? "default" : "primary"}
                        disabled={fieldBusy("price")}
                        onClick={(e) => {
                          e.stopPropagation();
                          savePatch(row, { price: target });
                        }}
                        sx={{
                          height: 18,
                          fontSize: 10,
                          fontFamily: '"Geist Mono", monospace',
                          fontWeight: 600,
                          borderRadius: 0.75,
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Stack>
            )}
            {/* "edited X ago" doubles as a sort affordance — clicking
                it sorts the table by ``last_edited_at`` and toggles
                asc/desc on repeat clicks. The default direction is
                "desc" (newest edits first) which is what an operator
                processing a bulk-edit session usually wants. */}
            {edited && (() => {
              const sortingByEdited = sortKey === "last_edited_at";
              const arrow = sortingByEdited
                ? (sortDir === "desc"
                    ? <ChevronDown size={9} />
                    : <ChevronUp size={9} />)
                : null;
              return (
                <Tooltip
                  title={`Last operator edit: ${new Date(Number(edited) * 1000).toLocaleString()}. Click to sort by last edited.`}
                  placement="left"
                  arrow
                >
                  <Box
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (sortingByEdited) {
                        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                      } else {
                        setSortKey("last_edited_at");
                        setSortDir("desc");
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      userSelect: "none",
                      px: 0.25,
                      borderRadius: 0.5,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      color: sortingByEdited ? "primary.main" : "text.disabled",
                      fontWeight: sortingByEdited ? 600 : 400,
                      fontSize: 10,
                      lineHeight: 1.2,
                      "&:hover": { color: "primary.main", bgcolor: "action.hover" },
                    }}
                  >
                    edited {relTime(edited)}
                    {arrow}
                  </Box>
                </Tooltip>
              );
            })()}
          </Stack>
        );
      }
      case "quantity": {
        const target = Number(row.unsold_remaining);
        const imbalance = row.listing_imbalance;
        const showWarning = imbalance && imbalance !== "balanced" && Number.isFinite(target);
        // The server collapses multiple eBay listings of the same
        // product+condition group into a single row. ``quantity`` is
        // the SUM across that group, so the imbalance math is right —
        // but a click-to-fix would only patch ONE listing's qty,
        // leaving the others untouched. Surface this with a "×N
        // listings" indicator and disable the auto-adjust chip so the
        // user fixes them deliberately on eBay.
        const groupSize = Number(row.total_listings_in_group) || 1;
        const isMultiListing = groupSize > 1 || row.has_duplicate_listing;
        const adjust = () => {
          if (isMultiListing) return;
          if (!Number.isFinite(target) || target < 0) return;
          savePatch(row, { quantity: target });
        };
        return (
          <Stack direction="column" spacing={0.25} alignItems="flex-end">
            <EditableTextCell
              value={row.quantity ?? ""}
              type="number"
              align="right"
              busy={fieldBusy("quantity") || isMultiListing}
              inputProps={{ min: "0", step: "1" }}
              onCommit={(v) => {
                const n = parseInt(v, 10);
                if (!Number.isFinite(n) || n < 0) return;
                savePatch(row, { quantity: n });
              }}
              displayValue={
                <Box sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.quantity ?? "—"}
                </Box>
              }
            />
            {isMultiListing && (
              <Tooltip
                title={`This product has ${groupSize} active eBay listings. The qty above is the total across all of them. Auto-fix is disabled because a single edit would only adjust one listing — consolidate or end the duplicates on eBay first.`}
                arrow placement="top"
              >
                <Chip
                  size="small"
                  label={`×${groupSize} listings`}
                  color="default"
                  variant="outlined"
                  sx={{
                    height: 18, fontSize: 10, fontWeight: 600,
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              </Tooltip>
            )}
            {showWarning && !isMultiListing && (
              <Tooltip
                title={
                  imbalance === "overlisted"
                    ? `Overlisted: eBay has ${row.quantity}, only ${target} unsold in inventory. Click to lower to ${target}.`
                    : `Underlisted: eBay has ${row.quantity}, you have ${target} unsold in inventory. Click to raise to ${target}.`
                }
                arrow placement="top"
              >
                <Chip
                  size="small"
                  label={imbalance === "overlisted" ? `over → ${target}` : `under → ${target}`}
                  color={imbalance === "overlisted" ? "warning" : "info"}
                  variant="filled"
                  disabled={fieldBusy("quantity")}
                  onClick={(e) => { e.stopPropagation(); adjust(); }}
                  sx={{
                    height: 18, fontSize: 10, fontWeight: 600,
                    cursor: "pointer",
                    "& .MuiChip-label": { px: 0.5 },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        );
      }
      case "condition": {
        const current = conditionToChoice(row.condition);
        return (
          <Select
            size="small"
            value={current}
            disabled={fieldBusy("condition")}
            displayEmpty
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              const next = e.target.value;
              if (!next || next === current) return;
              savePatch(row, { condition: next });
            }}
            sx={{ minWidth: 100 }}
            renderValue={(v) => {
              if (!v) return <Box component="span" sx={{ color: "text.disabled" }}>—</Box>;
              return CONDITION_CHOICES.find((c) => c.value === v)?.label || v;
            }}
          >
            {CONDITION_CHOICES.map((c) => (
              <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
            ))}
          </Select>
        );
      }
      case "lowest": {
        if (!showUndercut) {
          return <Box sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>—</Box>;
        }
        const low = row.lowest_competitor_price;
        if (low == null) {
          return (
            <Tooltip title="No competitor data — eBay returned no comps, was rate-limited, or query failed">
              <Box sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>—</Box>
            </Tooltip>
          );
        }
        const my = Number(row.price) || 0;
        let color = 'text.primary';
        if (my > low + 0.005) color = 'error.main';     // we're over
        else if (my < low - 0.005) color = 'success.main'; // we're cheaper
        const compUrl   = row.lowest_competitor_url;
        const compTitle = row.lowest_competitor_title || '';
        const matchN    = row.competitor_match_count || 0;
        const tooltip = (
          <Box>
            <Box sx={{ fontWeight: 600 }}>{compTitle || 'Competitor listing'}</Box>
            <Box sx={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
              {matchN} comp{matchN === 1 ? '' : 's'} found · click to view on eBay
            </Box>
          </Box>
        );
        // Clickable price → opens the competitor's listing on eBay.
        // Falls back to plain text when the cached row is older than
        // this URL-capture change and the URL field is null.
        const inner = (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ fontVariantNumeric: 'tabular-nums', color, fontWeight: 500 }}>
            <Box component="span">{money(low)}</Box>
            {compUrl && <LaunchIcon sx={{ fontSize: 11, opacity: 0.7 }} />}
          </Stack>
        );
        return (
          <Stack alignItems="flex-end" spacing={0}>
            <Tooltip title={tooltip} arrow placement="left">
              {compUrl ? (
                <MuiLink
                  href={compUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  underline="none"
                  sx={{ color: 'inherit' }}
                >
                  {inner}
                </MuiLink>
              ) : inner}
            </Tooltip>
            {row.competitor_fetched_at && (
              <Tooltip
                title={`Competitor lookup at ${new Date(Number(row.competitor_fetched_at) * 1000).toLocaleString()}. Refreshes once per 24h to stay under the 5k/day API quota.`}
                placement="left"
                arrow
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', fontSize: 10, lineHeight: 1.2 }}
                >
                  checked {relTime(row.competitor_fetched_at)}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        );
      }
      // The old "views" + "days_unsold" + "spend" columns were folded
      // into the new combined "Activity" cell (under the days_unsold
      // sort key) and the title's cost-basis tooltip respectively.
      // Each still has a renderer so the COLUMNS table can move
      // them back to standalone without code changes.
      case "views":
        return (
          <Box sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
            {row.views ?? 0}
          </Box>
        );
      case "days_unsold": {
        // Combined "Activity" cell: views + days-unsold stacked, color
        // by the days-unsold staleness band (>21d red, >14d amber).
        // Replaces the old separate Views / Days columns.
        const d = Number(row.days_unsold ?? 0);
        const v = Number(row.views ?? 0);
        const color = d > 21 ? 'error.main' : d > 14 ? 'warning.main' : 'text.secondary';
        return (
          <Stack alignItems="flex-end" spacing={0}>
            <Typography
              variant="body2"
              sx={{ fontVariantNumeric: 'tabular-nums', color, fontWeight: 500, lineHeight: 1.2 }}
            >
              {d}d
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: 10, lineHeight: 1.2 }}
            >
              {v} view{v === 1 ? '' : 's'}
            </Typography>
          </Stack>
        );
      }
      case "spend": {
        const s = (Number(row.cost) || 0) + (Number(row.tax_cost) || 0) + (Number(row.premium_fee) || 0);
        return (
          <Box sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
            {money(s)}
          </Box>
        );
      }
      case "actions": {
        const target = Number(row.undercut_target);
        const myPrice = Number(row.price) || 0;
        const canUndercut = showUndercut
          && row.item_id
          && row.undercut_target != null
          && isFinite(target)
          && target < myPrice;
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {row.item_id && (
              <Tooltip title="Open this listing on eBay">
                <IconButton
                  size="small"
                  component={MuiLink}
                  href={`https://www.ebay.com/itm/${String(row.item_id).trim()}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <LaunchIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {/* Inventory drill-down: all linked eBay listings for this
                product. Scrolls the inventory detail page straight to
                the listings section via #listings. */}
            {row.inventory_key && (
              <Tooltip title="See all listings for this product">
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={`/admint/inventory/${encodeURIComponent(row.inventory_key)}#listings`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ color: 'primary.main' }}
                >
                  <Inventory2OutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {/* Inventory drill-down: sales history for this product. */}
            {row.inventory_key && (
              <Tooltip title="See sales history for this product">
                <IconButton
                  size="small"
                  component={RouterLink}
                  to={`/admint/inventory/${encodeURIComponent(row.inventory_key)}#sales`}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ color: 'secondary.main' }}
                >
                  <SellOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
            {/* Active comp listings — eBay search filtered to Buy-It-Now
                items matching this product, narrowed to the same
                condition as this listing and sorted lowest price
                first. The default "Best Match" sort + mixed-condition
                results made the button useless for price benchmarking;
                this opens straight on the row that actually competes
                with the operator's price. */}
            {(() => {
              const q = row.cleaned_title || row.title;
              const url = ebaySearchUrl(q, {
                sold: false,
                condition: row.condition,
                sortLowest: true,
              });
              if (!url) return null;
              return (
                <Tooltip
                  title={`See cheapest active ${(row.condition || "any-condition")} listings for this product`}
                >
                  <IconButton
                    size="small"
                    component={MuiLink}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: 'info.main' }}
                  >
                    <StorefrontIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              );
            })()}
            {/* Recent sales — eBay sold/completed view, the canonical
                way to gauge "what does this actually sell for". Uses
                the same query as the active-listings shortcut so the
                two stay in sync. */}
            {(() => {
              const q = row.cleaned_title || row.title;
              const url = ebaySearchUrl(q, { sold: true });
              if (!url) return null;
              return (
                <Tooltip title="See recent sales of this product (eBay sold/completed)">
                  <IconButton
                    size="small"
                    component={MuiLink}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: 'success.main' }}
                  >
                    <TrendingUpIcon fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              );
            })()}
            {/* Refresh (End + RelistFixedPriceItem). Only enabled when
                the listing has been sitting ≥30d without a sale — same
                staleness band the "days-unsold" cell colors red. Ends
                the current ItemID and starts a fresh one from the same
                inventory data, restoring the "just listed" Best Match
                boost. */}
            {(() => {
              const stale = Number(row.days_unsold ?? 0) >= 30;
              const busyRow = relisting.has(row.lot_id);
              return (
                <Tooltip title={
                  busyRow
                    ? "Refreshing…"
                    : stale
                      ? "Refresh listing (end + relist for fresh visibility). Ends the current ItemID."
                      : "Only enabled after 30 days without a sale"
                }>
                  <span>
                    <IconButton
                      size="small"
                      disabled={!stale || busyRow || !row.item_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Refresh ${row.lot_id}? This ends the current eBay listing and starts a fresh one.`)) {
                          relistOne(row);
                        }
                      }}
                      sx={{ color: stale ? 'warning.main' : 'text.disabled' }}
                    >
                      {busyRow
                        ? <CircularProgress size={14} color="inherit" />
                        : <AutorenewOutlinedIcon fontSize="inherit" />}
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })()}
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); setDiscountTarget(row); }}
            >
              Discount
            </Button>
            {showUndercut && (
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => { e.stopPropagation(); setUndercutTarget(row); }}
                disabled={!canUndercut}
              >
                Undercut
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => { e.stopPropagation(); setPromoteTarget(row); }}
              disabled={!row.item_id}
            >
              Promote
            </Button>
          </Stack>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Page header — matches OC's standard layout */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: { xs: 2, md: 3.5 } }}
      >
        <Box>
          <Typography variant="h1" sx={{ mb: 0.5, fontSize: { xs: '1.85rem', md: '2.5rem' } }}>
            eBay Listings
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
            {total.toLocaleString()} active listing{total === 1 ? "" : "s"}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Bulk-fix every overlisted/underlisted listing in one click.
              Uses /api/listings/reconcile_all which mirrors the per-row
              "over → N" chip math across every active listing. Two-step:
              first a dry-run preview shows exactly what will change, then
              a confirm dialog commits the writes. Multi-listing groups
              (operator has duplicate active listings for the same
              product) are skipped — they need human judgment. */}
          <Button
            startIcon={reconciling ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="outlined"
            onClick={async () => {
              setReconciling(true);
              try {
                const dry = await apiPost('/api/listings/reconcile_all', { dry_run: true });
                const n = dry?.adjusted_count || 0;
                const skip = dry?.skipped_count || 0;
                if (!dry?.success) {
                  toastError('Reconcile preview failed', dry?.error || 'Unknown error');
                  return;
                }
                if (n === 0 && skip === 0) {
                  toastSuccess('All qty in sync', 'No listings need fixing.');
                  return;
                }
                const msg = (
                  `Will adjust ${n} listing${n === 1 ? '' : 's'} on eBay.` +
                  (skip ? `\n${skip} skipped (multi-listing groups — end duplicates on eBay first).` : '') +
                  `\n\nProceed?`
                );
                if (!window.confirm(msg)) return;
                const live = await apiPost('/api/listings/reconcile_all');
                if (live?.success) {
                  toastSuccess('Reconcile done', `Adjusted ${live.adjusted_count}, skipped ${live.skipped_count}`);
                  try { cacheInvalidate('/api/listings'); } catch {}
                  load();
                } else {
                  toastError('Reconcile failed', live?.error || 'Unknown error');
                }
              } catch (e) {
                toastError('Reconcile failed', e instanceof ApiError ? e.message : String(e));
              } finally {
                setReconciling(false);
              }
            }}
            disabled={loading || forceSyncing || reconciling}
          >
            {reconciling ? 'Reconciling…' : 'Reconcile qty'}
          </Button>
          <Button
            startIcon={(loading || forceSyncing) ? <CircularProgress size={15} color="inherit" /> : <SyncIcon />}
            variant="contained"
            onClick={async () => {
              // POST first so the backend re-pulls active listings from
              // eBay and re-syncs the local table (decrement watermarks
              // are preserved). Then re-fetch the rendered list. This is
              // what the operator means by "Refresh" — not just rerun a
              // local query, actually go talk to eBay.
              setForceSyncing(true);
              try {
                const r = await apiPost('/api/listings/refresh');
                if (r?.success) {
                  toastSuccess('Listings synced', `Pulled ${r.count} from eBay`);
                } else if (r?.error) {
                  toastError('Sync failed', r.error);
                }
              } catch (e) {
                toastError('Sync failed', e instanceof ApiError ? e.message : String(e));
              } finally {
                setForceSyncing(false);
              }
              // Invalidate the per-route client cache so load() actually
              // hits the API for the freshly-synced rows.
              try { cacheInvalidate('/api/listings'); } catch {}
              load();
            }}
            disabled={loading || forceSyncing || reconciling}
          >
            {forceSyncing ? 'Syncing eBay…' : 'Refresh'}
          </Button>
        </Stack>
      </Stack>

      {/* Filters card */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search title, lot id…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {/* Listed-vs-unsold imbalance filter — multi-select toggle chips.
              Empty selection means "show all". */}
          <ToggleButtonGroup
            value={Array.isArray(imbalanceFilter) ? imbalanceFilter : []}
            size="small"
            onChange={(_, v) => setImbalanceFilter(Array.isArray(v) ? v : [])}
            aria-label="Filter by qty imbalance"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: 13, px: 1.5, py: 0.5,
              },
            }}
          >
            <ToggleButton value="overlisted" color="warning">Over-listed</ToggleButton>
            <ToggleButton value="underlisted" color="info">Under-listed</ToggleButton>
            <ToggleButton value="balanced">Balanced</ToggleButton>
          </ToggleButtonGroup>
          {/* Condition + Category multi-selects. Options come from the
              backend facets so the dropdown only shows values that
              actually match at least one active listing. */}
          <Select
            multiple
            size="small"
            displayEmpty
            disabled={effectiveFacets.conditions.length === 0}
            value={Array.isArray(conditionFilter) ? conditionFilter : []}
            onChange={(e) => {
              const v = e.target.value;
              setConditionFilter(Array.isArray(v) ? v : []);
            }}
            renderValue={(selected) => {
              if (effectiveFacets.conditions.length === 0) return "Condition (loading…)";
              return selected.length === 0 ? "Condition: any" : `Condition: ${selected.join(", ")}`;
            }}
            sx={{ minWidth: 180, fontSize: 13 }}
          >
            {effectiveFacets.conditions.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
          <Select
            multiple
            size="small"
            displayEmpty
            disabled={effectiveFacets.categories.length === 0}
            value={Array.isArray(categoryFilter) ? categoryFilter : []}
            onChange={(e) => {
              const v = e.target.value;
              setCategoryFilter(Array.isArray(v) ? v : []);
            }}
            renderValue={(selected) => {
              if (effectiveFacets.categories.length === 0) return "Category (loading…)";
              return selected.length === 0 ? "Category: any" : `Category: ${selected.length} selected`;
            }}
            sx={{ minWidth: 200, fontSize: 13 }}
          >
            {effectiveFacets.categories.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </Select>
          {/* Days-unsold bucket — quick way to focus on stale/dead
              inventory that needs a price drop or delist. */}
          <ToggleButtonGroup
            value={ageFilter}
            exclusive
            size="small"
            onChange={(_, v) => setAgeFilter(v || "")}
            aria-label="Filter by days unsold"
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: 13, px: 1.5, py: 0.5,
              },
            }}
          >
            <ToggleButton value="fresh">Fresh &lt;7d</ToggleButton>
            <ToggleButton value="stale">Stale 7-30d</ToggleButton>
            <ToggleButton value="dead">Dead 30d+</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Fetch lowest comparable BIN price from eBay for every listing. Cached for 24h so re-toggling is free; first enable of the day refreshes the data.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showUndercut}
                  onChange={(e) => setShowUndercut(e.target.checked)}
                />
              }
              label="Show competitor prices"
              sx={{ ml: 0 }}
            />
          </Tooltip>
          <Tooltip title="Show only listings where a competitor is currently cheaper than your price. Auto-enables the competitor lookup if you haven't already.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={undercutOnly}
                  onChange={(e) => {
                    setUndercutOnly(e.target.checked);
                    // Auto-enable the competitor data fetch when the
                    // filter is on, otherwise the filter has nothing
                    // to filter against. Don't auto-disable on toggle
                    // off — the user might still want to see the data.
                    if (e.target.checked && !showUndercut) setShowUndercut(true);
                  }}
                />
              }
              label="Undercut only"
              sx={{ ml: 0 }}
            />
          </Tooltip>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => {
                  const active = sortKey === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align || "left"}
                      style={col.width ? { width: col.width } : undefined}
                      sx={{
                        cursor: col.sortable ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                      onClick={() => onHeaderClick(col)}
                    >
                      <Stack
                        direction={col.align === "right" ? "row-reverse" : "row"}
                        alignItems="center"
                        gap={0.5}
                        sx={{ display: 'inline-flex' }}
                      >
                        {col.header}
                        {col.sortable && active && (
                          sortDir === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                      <CircularProgress size={14} />
                      <span>Loading…</span>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Stack alignItems="center" gap={1} sx={{ color: 'text.secondary' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>No active listings</Typography>
                      <Typography variant="caption">
                        Listings appear here once they go live on eBay.
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((row) => {
                // Click anywhere on the row that isn't a control → jump to
                // the inventory item this listing represents. Mirrors the
                // navigation pattern on Sales (eBay order → new tab) but
                // here the primary destination is the in-app inventory
                // detail since that's the most common pivot. The launch
                // icon in the title cell still routes to eBay; this is
                // additive.
                const invKey = row.inventory_key || row.lot_id;
                const rowClickable = !!invKey;
                const handleRowClick = (e) => {
                  if (e.metaKey || e.ctrlKey || e.button === 1) return;
                  const t = e.target;
                  if (t.closest("button, a, input, textarea, select, .MuiChip-root, [role='button']")) return;
                  navigate(`/admint/inventory/${encodeURIComponent(invKey)}`);
                };
                return (
                  <TableRow
                    key={row.lot_id ?? row.item_id ?? row.id}
                    hover
                    onClick={rowClickable ? handleRowClick : undefined}
                    sx={rowClickable ? { cursor: "pointer" } : undefined}
                    title={rowClickable ? "Open inventory item" : undefined}
                  >
                    {COLUMNS.map((col) => (
                      <TableCell
                        key={col.key}
                        align={col.align || "left"}
                        style={col.width ? { width: col.width } : undefined}
                      >
                        {renderCell(row, col.key)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {loading && rows.length > 0 && (
          <Box sx={{
            position: 'absolute', top: 8, right: 12,
            display: 'inline-flex', alignItems: 'center', gap: 0.75,
            bgcolor: 'background.paper', border: 1, borderColor: 'divider',
            borderRadius: 1, px: 1, py: 0.25, fontSize: '0.7rem',
            color: 'text.secondary',
          }}>
            <CircularProgress size={12} /> Refreshing
          </Box>
        )}

        <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
          <TablePagination
            component="div"
            count={total}
            page={Math.max(0, page - 1)}
            onPageChange={(_, p) => setPage(p + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(Number(e.target.value))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </Box>
      </Paper>

      <DiscountModal
        open={!!discountTarget}
        onClose={() => setDiscountTarget(null)}
        listing={discountTarget}
        onApplied={() => { cacheInvalidate("/api/listings"); load(); }}
      />

      <PromoteModal
        open={!!promoteTarget}
        onClose={() => setPromoteTarget(null)}
        listing={promoteTarget}
        onConfirm={promote}
        busy={promoting}
      />

      <UndercutModal
        open={!!undercutTarget}
        onClose={() => setUndercutTarget(null)}
        listing={undercutTarget}
        onApplied={() => { cacheInvalidate("/api/listings"); load(); }}
      />
    </Box>
  );
}
